import { nullableArray, nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

export enum A2vAssetType {
  Image = 'IMAGE',
  Video = 'VIDEO',
}

export enum A2vUploadStatus {
  Pending = 'PENDING',
  LoadingMetadata = 'LOADING_METADATA',
  Uploading = 'UPLOADING',
  Success = 'SUCCESS',
  Error = 'ERROR',
}

export enum AssetsSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

export enum AssetsSortProperty {
  Name = 'name',
  AssetType = 'assetType',
  CreatedOn = 'createdOn',
  UpdatedOn = 'updatedOn',
}

export enum AssetsTypeFilter {
  All = 'ALL',
  Image = 'IMAGE',
  Video = 'VIDEO',
}

export const assetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  assetType: z.enum(A2vAssetType),
  width: z.number().nonnegative(),
  height: z.number().nonnegative(),
  tags: nullableArray(z.string()),
  description: nullableOptional(z.string()),
  url: nullableOptional(z.string()),
  uri: nullableOptional(z.string()),
  createdOn: nullableOptional(z.string()),
  updatedOn: nullableOptional(z.string()),
});

export const assetsPageSchema = z.object({
  content: z.array(assetSchema),
  totalElements: z.number().int().nonnegative(),
});

export const assetTagsSchema = z.array(z.string());

export type A2vAsset = z.infer<typeof assetSchema>;
export type AssetsPage = z.infer<typeof assetsPageSchema>;

export interface AssetsFilter {
  searchTerm: string;
  assetType: A2vAssetType | null;
  tags: string[];
  page: number;
  size: number;
  sortProperty: AssetsSortProperty | null;
  sortDirection: AssetsSortDirection;
}

export const defaultAssetsFilter: AssetsFilter = {
  searchTerm: '',
  assetType: null,
  tags: [],
  page: 0,
  size: 10,
  sortProperty: AssetsSortProperty.CreatedOn,
  sortDirection: AssetsSortDirection.Desc,
};

export interface AssetUploadPayload {
  file: File;
  width: number;
  height: number;
  tags: string[];
}

export function serializeAssetsFilter(filter: AssetsFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    assetType: filter.assetType ?? '',
    tags: filter.tags,
  });
}

export function createAssetUploadInit(payload: AssetUploadPayload): RequestInit {
  const formData = new FormData();
  formData.append('width', String(payload.width));
  formData.append('height', String(payload.height));
  formData.append('file', payload.file);
  payload.tags.forEach((tag, index) => {
    formData.append(`tags[${String(index)}]`, tag);
  });
  return { method: 'POST', body: formData };
}

export function assetUpdatePayload(asset: A2vAsset, tags: string[]) {
  return { ...asset, tags };
}
